import { useEffect, useState } from "react";
import { api, money, fmtDate } from "@/lib/api";
import { useTenant } from "@/context/TenantContext";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Download, AlarmClock } from "lucide-react";
import VisitValueReport from "@/components/VisitValueReport";
import { useAuth } from "@/context/AuthContext";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend } from "recharts";

const COLORS = ["#0284C7", "#D97706", "#16A34A", "#DC2626", "#9333EA", "#14B8A6", "#64748B"];

function SlaCompliance({ cur }) {
  const [months, setMonths] = useState("6");
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get(`/reports/sla?months=${months}`).then((r) => setData(r.data)).catch(() => setData(null));
  }, [months]);

  if (!data) return null;
  const t = data.totals;
  const rateTone = (r) => (r === null || r === undefined) ? "text-slate-400" : r >= 95 ? "text-emerald-600" : r >= 85 ? "text-amber-600" : "text-red-600";

  const exportCsv = () => {
    const head = ["Customer", "SLA Jobs", "Achieved", "Breached", "Open", "Compliance %"];
    const lines = [head.join(",")];
    data.customers.forEach((c) => lines.push([`"${c.customer_name}"`, c.total, c.achieved, c.breached, c.open, c.rate ?? ""].join(",")));
    lines.push(["\"OVERALL\"", t.achieved + t.breached, t.achieved, t.breached, t.open, t.rate ?? ""].join(","));
    const url = URL.createObjectURL(new Blob([lines.join("\n")], { type: "text/csv" }));
    const a = document.createElement("a"); a.href = url; a.download = `sla-compliance-${months}m.csv`; a.click();
    setTimeout(() => URL.revokeObjectURL(url), 30000);
  };

  return (
    <div className="bg-white border border-slate-200 rounded-md p-4 mb-4" data-testid="sla-compliance-report">
      <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
        <div>
          <h3 className="font-head font-semibold flex items-center gap-2"><AlarmClock className="h-4 w-4 text-red-600" />SLA Compliance</h3>
          <p className="text-xs text-slate-500">Monthly achieved vs breached per customer — proof of contract compliance at renewal time.</p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={months} onValueChange={setMonths}>
            <SelectTrigger data-testid="sla-report-months" className="w-[130px] h-9 bg-white"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-white">{[["3", "3 months"], ["6", "6 months"], ["12", "12 months"]].map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
          </Select>
          <Button variant="outline" size="sm" className="h-9" data-testid="sla-report-export" onClick={exportCsv}><Download className="h-4 w-4 mr-1" />Export CSV</Button>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
        {[["Overall Compliance", t.rate !== null ? `${t.rate}%` : "—", rateTone(t.rate)],
          ["SLAs Achieved", t.achieved, "text-emerald-600"],
          ["SLAs Breached", t.breached, "text-red-600"],
          ["Open SLA Jobs", t.open, "text-amber-600"]].map(([l, v, cls]) => (
          <div key={l} className="bg-slate-50 rounded-md p-3">
            <div className="text-xs text-slate-500">{l}</div>
            <div className={`text-2xl font-head font-bold mt-0.5 ${cls}`} data-testid={`sla-kpi-${l.toLowerCase().replace(/ /g, "-")}`}>{v}</div>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div>
          <h4 className="text-xs font-medium text-slate-500 uppercase tracking-wide mb-2">Monthly performance</h4>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={data.months}>
              <XAxis dataKey="label" tick={{ fontSize: 11 }} />
              <YAxis allowDecimals={false} tick={{ fontSize: 11 }} width={28} />
              <Tooltip />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              <Bar dataKey="achieved" name="Achieved" stackId="a" fill="#16A34A" radius={[0, 0, 0, 0]} />
              <Bar dataKey="breached" name="Breached" stackId="a" fill="#DC2626" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
        <div>
          <h4 className="text-xs font-medium text-slate-500 uppercase tracking-wide mb-2">By customer</h4>
          <div className="max-h-[220px] overflow-y-auto scroll-thin border border-slate-100 rounded-md">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 sticky top-0"><tr>{["Customer", "Jobs", "Achieved", "Breached", "Open", "Compliance"].map((h) => <th key={h} className="text-left font-medium text-slate-500 px-2.5 py-2 text-xs">{h}</th>)}</tr></thead>
              <tbody>
                {data.customers.length === 0 && <tr><td colSpan={6} className="text-center py-6 text-slate-400">No SLA jobs in this window.</td></tr>}
                {data.customers.map((c) => (
                  <tr key={c.customer_name} className="border-t border-slate-100" data-testid={`sla-customer-${c.customer_name}`}>
                    <td className="px-2.5 py-2 font-medium text-slate-800">{c.customer_name}</td>
                    <td className="px-2.5 py-2 text-slate-600">{c.total}</td>
                    <td className="px-2.5 py-2 text-emerald-600">{c.achieved}</td>
                    <td className="px-2.5 py-2 text-red-600">{c.breached}</td>
                    <td className="px-2.5 py-2 text-amber-600">{c.open}</td>
                    <td className={`px-2.5 py-2 font-semibold ${rateTone(c.rate)}`}>{c.rate !== null ? `${c.rate}%` : "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function Reports() {
  const { company } = useTenant();
  const { user } = useAuth();
  const canFinance = ["amden_super_admin", "ops_owner", "finance"].includes(user?.role);
  const cur = company?.currency || "EUR";
  const [jobs, setJobs] = useState([]);
  const [quotes, setQuotes] = useState([]);

  useEffect(() => {
    api.get("/jobs").then((r) => setJobs(r.data));
    api.get("/quotations").then((r) => setQuotes(r.data));
  }, []);

  const byType = Object.entries(jobs.reduce((a, j) => { a[j.job_type || "Other"] = (a[j.job_type || "Other"] || 0) + 1; return a; }, {})).map(([name, value]) => ({ name, value }));
  const byTech = Object.entries(jobs.reduce((a, j) => { const t = j.technician_name || "Unassigned"; a[t] = (a[t] || 0) + 1; return a; }, {})).map(([name, jobs]) => ({ name, jobs }));
  const byBilling = Object.entries(jobs.reduce((a, j) => { a[j.billing_status] = (a[j.billing_status] || 0) + 1; return a; }, {})).map(([name, value]) => ({ name, value }));
  const unbilled = jobs.filter((j) => j.operational_status === "Completed" && !["Sent to Accounts", "Invoiced in Sage", "Not Billable"].includes(j.billing_status));
  const unbilledValue = unbilled.reduce((s, j) => s + (j.expected_value || 0), 0);
  const convRate = quotes.length ? Math.round(quotes.filter((q) => ["Accepted", "Converted to Job"].includes(q.status)).length / quotes.length * 100) : 0;

  const Card = ({ title, children }) => <div className="bg-white border border-slate-200 rounded-md p-4"><h3 className="font-head font-semibold text-sm mb-3">{title}</h3>{children}</div>;

  return (
    <div>
      <h1 className="text-2xl font-head font-bold text-slate-900 mb-4">Reports</h1>
      <SlaCompliance cur={cur} />
      {canFinance && <VisitValueReport cur={cur} />}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
        {[["Total Jobs", jobs.length], ["Unbilled Completed", unbilled.length], ["Unbilled Value", money(unbilledValue, cur)], ["Quote Conversion", `${convRate}%`]].map(([l, v]) => (
          <div key={l} className="bg-white border border-slate-200 rounded-md p-4"><div className="text-xs text-slate-500">{l}</div><div className="text-2xl font-head font-bold mt-1 accent-text">{v}</div></div>
        ))}
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card title="Job Activity by Technician">
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={byTech}><XAxis dataKey="name" tick={{ fontSize: 11 }} /><YAxis allowDecimals={false} tick={{ fontSize: 11 }} /><Tooltip /><Bar dataKey="jobs" fill="var(--client-accent)" radius={[4, 4, 0, 0]} /></BarChart>
          </ResponsiveContainer>
        </Card>
        <Card title="Jobs by Type">
          <ResponsiveContainer width="100%" height={260}>
            <PieChart><Pie data={byType} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={90} label={{ fontSize: 10 }}>{byType.map((e, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}</Pie><Tooltip /></PieChart>
          </ResponsiveContainer>
        </Card>
        <Card title="Billing Status Breakdown">
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={byBilling} layout="vertical"><XAxis type="number" allowDecimals={false} tick={{ fontSize: 11 }} /><YAxis type="category" dataKey="name" width={140} tick={{ fontSize: 10 }} /><Tooltip /><Bar dataKey="value" fill="#9333EA" radius={[0, 4, 4, 0]} /></BarChart>
          </ResponsiveContainer>
        </Card>
        <Card title="Unbilled Completed Jobs">
          <div className="max-h-64 overflow-y-auto scroll-thin">
            <table className="w-full text-sm"><tbody>
              {unbilled.map((j) => <tr key={j.id} className="border-b border-slate-100"><td className="py-2 font-medium">{j.ref}</td><td className="py-2 text-slate-500">{j.customer_name}</td><td className="py-2 text-right">{money(j.expected_value, cur)}</td></tr>)}
              {unbilled.length === 0 && <tr><td className="py-6 text-center text-slate-400">No unbilled completed jobs.</td></tr>}
            </tbody></table>
          </div>
        </Card>
      </div>
    </div>
  );
}
